import { IApiError } from '@aquariuxfintech/aqx-trader-sdk-config/types';

import { marketAPIController } from '@aqx-core/common/api/market';
import {
  EAlertStatus,
  ICreatePriceAlertParams,
  IInitSymbolPriceAlerts,
  IPriceAlert,
  IUpdatePriceAlertParams,
  TPriceAlertSymbolDetail,
  priceAlertAPIController
} from '@aqx-core/common/api/price-alert';
import { EPriceAlertTab, MAX_ALERTS_FOR_ALL_SYMBOLS } from '@aqx-core/common/constants/price-alert';
import { i18n } from '@aqx-core/common/i18n';
import { NETWORK_ERROR_MESSAGE, mapResponseCodeToMessage } from '@aqx-core/common/request';
import {
  ISymbolTopicLivePrices,
  SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES,
  symbolTopicSubscription
} from '@aqx-core/common/socket/subscriptions';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { getAlertConditionLabel, getAlertFrequencyLabel } from '@aqx-core/common/utils/price-alert';

import { EUISnackBarMessageType, snackbarController } from '../snackbar';
import { priceAlertService } from './service';
import {
  ILivePricesMap,
  IPriceAlertMap,
  IPriceAlertSymbolDetailMap,
  ISymbolDisabledMap,
  ISymbolPriceAlertsMap
} from './types';

interface ICreatePriceAlertParamsObj {
  values: ICreatePriceAlertParams;
  formSymbol?: string;
  isMobileWeb?: boolean;
  onSuccessCallback?: () => void;
  onError?: (error: IApiError) => boolean;
  disableRequery?: boolean;
}

interface IUpdatePriceAlertParamsObj extends Omit<ICreatePriceAlertParamsObj, 'values'> {
  newAlert: IUpdatePriceAlertParams;
}

class PriceAlertViewController {
  private currentSymbolSubscriptionKey = new Set<string>();
  private isInitialized = false;

  public init = () => {
    if (this.isInitialized) {
      return;
    }

    if (!priceAlertService.context.currentSymbol) {
      const lastSelectedSymbol = storage.getItem<string>(EStorageKey.LAST_SELECTED_SYMBOL);
      if (lastSelectedSymbol) {
        priceAlertService.setCurrentSymbol(lastSelectedSymbol);
      }
    }

    this.isInitialized = true;
    this.getAllPriceAlerts();
    this.fetchAlertConfig();
  };

  private fetchCurrentSymbolDetailIfMissing = async () => {
    const currentSymbol = priceAlertService.context.currentSymbol;
    if (!currentSymbol) {
      return;
    }

    // If symbol detail is already available (e.g. set via setSingleSymbolDetail before navigation), skip
    if (priceAlertService.context.symbolDetailsMap[currentSymbol]) {
      return;
    }

    await marketAPIController.getSymbolDetail(
      { symbol: currentSymbol },
      {
        onSuccess: (symbolDetail) => {
          if (symbolDetail) {
            this.setSingleSymbolDetail(symbolDetail as TPriceAlertSymbolDetail);
          }
        },
        onError: () => {}
      }
    );
  };

  private fetchAlertConfig = () => {
    priceAlertService.setAlertConfigLoading(true);
    priceAlertAPIController.getAlertConfig({
      onSuccess: (config) => {
        if (config) {
          priceAlertService.setAlertConfig(config);
        }
      },
      onError: () => {},
      onFinally: () => {
        priceAlertService.setAlertConfigLoading(false);
      }
    });
  };

  private getAllPriceAlerts = async (shouldLoading: boolean = true) => {
    if (shouldLoading) {
      priceAlertService.setPriceAlertsLoading(true);
    }

    await priceAlertAPIController.getPriceAlerts(
      {
        cursorId: 0,
        limit: MAX_ALERTS_FOR_ALL_SYMBOLS
      },
      {
        onSuccess: (priceAlertsArr) => {
          if (priceAlertsArr) {
            const symbolDetailsMap: IPriceAlertSymbolDetailMap = {};
            const symbolDisabledMap: ISymbolDisabledMap = {};
            const priceAlertsMap = priceAlertsArr.reduce(
              (prev: ISymbolPriceAlertsMap, priceAlertObj: IInitSymbolPriceAlerts) => {
                const { alertList, symbol } = priceAlertObj;

                if (alertList.length === 0) {
                  return prev;
                }

                const alerts: IPriceAlertMap = {};
                let detail: TPriceAlertSymbolDetail | undefined;
                let noOfDisabledAlerts: number = 0;
                alertList.forEach((priceAlert, index) => {
                  const {
                    bid,
                    decimal,
                    description,
                    open,
                    pointStep,
                    prevClose,
                    symbol,
                    tickRulesGroup,
                    type,
                    status
                  } = priceAlert;
                  if (index === 0) {
                    detail = {
                      bid,
                      decimal,
                      description,
                      open,
                      pointStep,
                      prevClose,
                      symbol,
                      tickRulesGroup,
                      type
                    };
                  }
                  // Count total number of disabled alerts
                  // If noOfDisabledAlerts === alertList.length, set symbolDisabled to true
                  if (status === EAlertStatus.DISABLED) {
                    noOfDisabledAlerts += 1;
                  }
                  alerts[priceAlert.alertId] = priceAlert;
                });

                if (detail) {
                  symbolDetailsMap[symbol] = detail;
                }

                symbolDisabledMap[symbol] = noOfDisabledAlerts === alertList.length;

                if (Object.keys(alerts).length > 0) {
                  prev[symbol] = {
                    ...priceAlertObj,
                    alerts
                  };
                }
                return prev;
              },
              {}
            );
            priceAlertService.setPriceAlertsMap(priceAlertsMap);
            priceAlertService.updateSymbolDetailsMap(symbolDetailsMap);
            priceAlertService.updateSymbolDisabledMap(symbolDisabledMap);

            this.updateLivePrices(symbolDetailsMap);
          }
        },
        onError: ({ code, message }) => {
          // Show error alert if message is not cancelled (i.e. when query cancelled due to multiple queries)
          if (message !== 'canceled') {
            snackbarController.showAlert({
              message: mapResponseCodeToMessage(code, message),
              messageType: EUISnackBarMessageType.ERROR
            });
          }
        },
        onFinally: () => {
          this.fetchCurrentSymbolDetailIfMissing().finally(() => {
            priceAlertService.setPriceAlertsLoading(false);
          });
        }
      }
    );
  };

  public fetchPriceAlertsBySymbol = async (symbol: string) => {
    await priceAlertAPIController.getPriceAlertsBySymbol(symbol, {
      onSuccess: (result) => {
        if (result) {
          const currentMap = priceAlertService.context.priceAlertsMap;
          const alerts: IPriceAlertMap = {};
          for (const alert of result.alertList) {
            alerts[alert.alertId] = alert;
          }
          if (Object.keys(alerts).length > 0) {
            priceAlertService.setPriceAlertsMap({
              ...currentMap,
              [result.symbol]: { ...result, alerts }
            });
          } else {
            // Remove symbol from map if no alerts exist
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [result.symbol]: _, ...rest } = currentMap;
            priceAlertService.setPriceAlertsMap(rest);
          }
        }
      },
      onError: () => {}
    });
  };

  public resetPriceAlerts = () => {
    this.isInitialized = false;

    priceAlertService.clearCurrentSymbol();

    priceAlertService.setShowBulkEdit(false);
    priceAlertService.clearEditAlertId();
    priceAlertService.clearAllSelectedAlerts();
    priceAlertService.setActiveTab(EPriceAlertTab.CURRENT_SYMBOL);

    priceAlertService.clearPriceAlertsMap();
    priceAlertService.clearSymbolDetailsMap();
    priceAlertService.clearSymbolDisabledMap();
    priceAlertService.clearLivePricesMap();
  };

  private updateLivePrices = (symbolDetailsMap: IPriceAlertSymbolDetailMap) => {
    const livePricesMap: ILivePricesMap = Object.values(symbolDetailsMap).reduce(
      (prev: ILivePricesMap, symbolDetail: TPriceAlertSymbolDetail) => {
        const { bid, open, prevClose, symbol } = symbolDetail;
        prev[symbol] = { bid, open, prevClose };
        return prev;
      },
      {}
    );
    priceAlertService.updateLivePricesMap(livePricesMap);
  };

  public setCurrentSymbol = (symbol: string) => {
    priceAlertService.setCurrentSymbol(symbol);
  };

  public setSingleSymbolDetail = (symbolDetail?: TPriceAlertSymbolDetail) => {
    if (!symbolDetail) {
      return;
    }

    const incomingSymbolDetailMap = { [symbolDetail.symbol]: symbolDetail };
    priceAlertService.updateSymbolDetailsMap(incomingSymbolDetailMap);

    this.updateLivePrices(incomingSymbolDetailMap);
  };

  public subscribeSymbolTopic = (symbol: string) => {
    if (!this.isSymbolSubscribed(symbol)) {
      symbolTopicSubscription.subscribe(symbol, SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES, this.updateSymbolPrice);

      this.currentSymbolSubscriptionKey.add(symbol);
    }
  };

  public unsubscribeSymbolTopic = (symbol: string) => {
    if (this.isSymbolSubscribed(symbol)) {
      symbolTopicSubscription.unsubscribe(symbol, SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES, this.updateSymbolPrice);

      this.currentSymbolSubscriptionKey.delete(symbol);
    }
  };

  public isSymbolSubscribed = (symbol: string) => {
    return this.currentSymbolSubscriptionKey.has(symbol);
  };

  private updateSymbolPrice = (updatedSymbolId: string, livePrices: ISymbolTopicLivePrices) => {
    priceAlertService.updateBidPriceOnLivePrices(livePrices.liveSell, updatedSymbolId);
  };

  public setShowBulkEdit = (showBulkEdit: boolean) => {
    priceAlertService.setShowBulkEdit(showBulkEdit);
  };

  public selectAlertIds = (alertIds: string[]) => {
    priceAlertService.addAlertIdsToSelected(alertIds);
  };

  public deselectAlertIds = (alertIds: string[]) => {
    priceAlertService.removeAlertIdsFromSelected(alertIds);
  };

  public selectAllAlertIds = (alertIds: string[]) => {
    priceAlertService.setAllSelectedAlerts(alertIds);
  };

  public deselectAllAlertIds = () => {
    priceAlertService.clearAllSelectedAlerts();
  };

  public handlePriceAlertToggle = (currentPriceAlert: IPriceAlert) => {
    const updatePriceAlertParams: IUpdatePriceAlertParams = {
      ...currentPriceAlert,
      targetValue: currentPriceAlert.targetPrice,
      toggle: !currentPriceAlert.toggle
    };
    priceAlertAPIController.updatePriceAlert(updatePriceAlertParams, {
      onSuccess: (updatedPriceAlert) => {
        if (updatedPriceAlert) {
          priceAlertService.updatePriceAlert(updatedPriceAlert);
        }
      },
      onError: ({ code, message }) => {
        // Show error alert if message is not cancelled (i.e. when query cancelled due to multiple queries)
        if (message !== 'canceled') {
          snackbarController.showAlert({
            message: mapResponseCodeToMessage(code, message),
            messageType: EUISnackBarMessageType.ERROR
          });
        }
      }
    });
  };

  public createAlert = (params: ICreatePriceAlertParamsObj) => {
    const { formSymbol, isMobileWeb, onError, onSuccessCallback, values } = params;
    if (!formSymbol) {
      return;
    }

    priceAlertService.setSubmitting(true);

    priceAlertAPIController.createPriceAlert(
      {
        ...values,
        symbol: formSymbol
      },
      {
        onSuccess: () => {
          onSuccessCallback?.();
          setTimeout(
            () => {
              snackbarController.showAlert({
                message: i18n.t('Price alert has been created.'),
                messageType: EUISnackBarMessageType.SUCCESS
              });
            },
            isMobileWeb ? 100 : 0
          );

          this.getAllPriceAlerts();
        },
        onError: (error) => {
          const shouldReturn = onError ? onError(error) : false;
          if (shouldReturn) {
            return;
          }

          snackbarController.showAlert({
            message: i18n.t('Failed to create price alert.'),
            messageType: EUISnackBarMessageType.ERROR,
            description: i18n.t('Please try again.')
          });
        },
        onFinally: () => {
          priceAlertService.setSubmitting(false);
        }
      }
    );
  };

  public updateAlert = (params: IUpdatePriceAlertParamsObj) => {
    const { formSymbol, isMobileWeb, newAlert, onError, onSuccessCallback } = params;
    if (!formSymbol || !newAlert) {
      return;
    }

    priceAlertService.setSubmitting(true);

    priceAlertAPIController.updatePriceAlert(newAlert, {
      onSuccess: () => {
        const { condition, frequencyCondition, targetValue } = newAlert;
        onSuccessCallback?.();
        setTimeout(
          () => {
            snackbarController.showAlert({
              message: i18n.t('Price alert has been updated.'),
              messageType: EUISnackBarMessageType.SUCCESS,
              description: `${formSymbol} ${getAlertConditionLabel(condition)} ${targetValue} | ${getAlertFrequencyLabel(frequencyCondition)}`
            });
          },
          isMobileWeb ? 100 : 0
        );
        priceAlertViewController.clearEditAlertId();

        this.getAllPriceAlerts();
      },
      onError: (error) => {
        const shouldReturn = onError ? onError(error) : false;
        if (shouldReturn) {
          return;
        }

        snackbarController.showAlert({
          message: i18n.t('Failed to update price alert.'),
          messageType: EUISnackBarMessageType.ERROR,
          description: i18n.t('Please try again.')
        });
      },
      onFinally: () => {
        priceAlertService.setSubmitting(false);
      }
    });
  };

  public deleteAlertIds = (
    alertIds: string[],
    isMobileWeb: boolean = false,
    onSuccessCallback?: () => void,
    onErrorCallback?: () => void
  ) => {
    const noOfAlerts = alertIds.length;
    const hasMoreThanOneAlerts = noOfAlerts > 1;
    if (noOfAlerts === 0) {
      return;
    }

    priceAlertAPIController.bulkDeletePriceAlerts(
      {
        ids: alertIds
      },
      {
        onSuccess: async (bulkDeleteResponse) => {
          if (bulkDeleteResponse) {
            const { successList } = bulkDeleteResponse;
            setTimeout(
              () => {
                snackbarController.showAlert({
                  message:
                    successList.length > 1
                      ? i18n.t('{{noOfAlerts}} Price alerts have been deleted.', { noOfAlerts: successList.length })
                      : i18n.t('Price alert has been deleted.'),
                  messageType: EUISnackBarMessageType.SUCCESS
                });
              },
              isMobileWeb ? 100 : 0
            );

            priceAlertViewController.deselectAllAlertIds();
            priceAlertViewController.setShowBulkEdit(false);
            priceAlertViewController.clearEditAlertId();

            await this.getAllPriceAlerts(false);
            onSuccessCallback?.();
          }
        },
        onError: (error) => {
          onErrorCallback?.();

          if (error.message?.includes(NETWORK_ERROR_MESSAGE)) {
            snackbarController.showAlert({
              message: i18n.t('Network error. Please try again later.'),
              messageType: EUISnackBarMessageType.ERROR
            });
            return;
          }

          snackbarController.showAlert({
            message: hasMoreThanOneAlerts
              ? i18n.t('Failed to delete price alerts.')
              : i18n.t('Failed to delete price alert.'),
            messageType: EUISnackBarMessageType.ERROR,
            description: i18n.t('Please try again.')
          });
        }
      }
    );
  };

  public clearEditAlertId = () => {
    priceAlertService.clearEditAlertId();
  };

  public setEditAlertId = (alertId: string) => {
    priceAlertService.setEditAlertId(alertId);
  };

  public setActiveTab = (tab: EPriceAlertTab) => {
    priceAlertService.setActiveTab(tab);
  };

  public checkAlertLimitReached = (): boolean => {
    const { alertConfig, currentSymbol, priceAlertsMap } = priceAlertService.context;

    const currentSymbolAlertCount = currentSymbol ? Object.keys(priceAlertsMap[currentSymbol]?.alerts ?? {}).length : 0;

    if (currentSymbolAlertCount >= alertConfig.symbolLimit) {
      return true;
    }

    let totalAlertCount = 0;
    Object.values(priceAlertsMap).forEach(({ alerts }) => {
      totalAlertCount += Object.keys(alerts).length;
    });

    return totalAlertCount >= alertConfig.accountLimit;
  };
}

export const priceAlertViewController = new PriceAlertViewController();
